import React, { useState } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, Users, MessageSquare, FileText, LogOut, ShieldCheck, 
  Calendar, Mail, Activity, Archive, Stethoscope, Lock, BarChart2,
  Briefcase, UserCheck, Layers, Settings, ChevronDown, Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import { useAdminAuth } from "./AdminAuthContext";
import { BUILD_META } from "../buildMeta";

const navSections = [
  {
    title: "Executive",
    items: [
      { to: "/admin", end: true, label: "Executive Overview", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
    ]
  },
  {
    title: "Recruitment",
    items: [
      { to: "/admin/applications", label: "Departments & Roles", icon: Layers, testId: "admin-nav-applications" },
      { to: "/admin/interviews", label: "Interview Schedule", icon: Calendar, testId: "admin-nav-interviews" },
      { to: "/admin/emails", label: "Email Center", icon: Mail, testId: "admin-nav-emails" },
    ]
  },
  {
    title: "Communications",
    items: [
      { to: "/admin/contacts", label: "Contact Messages", icon: MessageSquare, testId: "admin-nav-contacts" },
      { to: "/admin/activities", label: "Activity Center", icon: Activity, testId: "admin-nav-activities" },
      { to: "/admin/audit-logs", label: "Audit Logs", icon: FileText, testId: "admin-nav-audit-logs" },
    ]
  },
  {
    title: "Intelligence",
    items: [
      { to: "/admin/reports-archive", label: "Reports Archive", icon: Archive, testId: "admin-nav-reports-archive" },
    ]
  },
  {
    title: "System",
    items: [
      { to: "/admin/diagnostics", label: "System Diagnostics", icon: Stethoscope, testId: "admin-nav-diagnostics" },
    ]
  }
];

const futureModules = [
  "Employees", "Payroll", "Leave", "Assets", "Performance", "Compliance"
];

const renderCountRef = { current: 0 };

const AdminLayout = () => {
  const { admin, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showFutureModules, setShowFutureModules] = useState(false);

  console.log("ADMIN_LAYOUT", location.pathname);

  renderCountRef.current += 1;
  console.debug("[ADMIN LAYOUT RENDER]", {
    renderCount: renderCountRef.current,
    pathname: location.pathname,
    searchParams: location.search,
    timestamp: new Date().toISOString()
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-cyan-300">
        <div className="animate-pulse">Authenticating Enterprise Workspace...</div>
      </div>
    );
  }

  if (!admin) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    console.debug("[ADMIN LAYOUT LOGOUT CLICK]", { timestamp: new Date().toISOString() });
    logout();
    navigate("/admin/login");
  };

  const handleSidebarClick = (item) => {
    console.debug("[SIDEBAR CLICK]", {
      menu: item.label,
      currentPath: location.pathname,
      targetPath: item.to,
      timestamp: new Date().toISOString()
    });
    setTimeout(() => {
      console.debug("[SIDEBAR NAVIGATION COMPLETE]", {
        pathname: window.location.pathname,
        search: window.location.search,
        timestamp: new Date().toISOString()
      });
    }, 100);
  };

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(180deg, #050a14 0%, #000000 100%)" }}>
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 border-r border-white/10 flex flex-col" style={{ background: "rgba(10,22,40,0.85)", backdropFilter: "blur(20px)" }} data-testid="admin-sidebar">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/20" style={{ background: "linear-gradient(135deg, #00FFD1 0%, #00a884 100%)" }}>
              <ShieldCheck className="w-5 h-5 text-black" />
            </div>
            <div>
              <div className="text-white text-sm font-semibold tracking-wider font-mono">QUANENTRY</div>
              <div className="text-[10px] text-cyan-300/80 uppercase tracking-[0.2em] font-mono">Enterprise Admin</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-5 overflow-y-auto custom-scrollbar">
          {navSections.map((sec, idx) => (
            <div key={idx} className="space-y-1">
              <div className="px-3 text-[10px] uppercase tracking-widest text-white/30 font-mono font-semibold">
                {sec.title}
              </div>
              {sec.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testId}
                  onClick={() => handleSidebarClick(item)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
                      isActive
                        ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40 shadow-sm shadow-cyan-500/10"
                        : "text-white/60 hover:text-white hover:bg-white/5 border border-transparent"
                    }`
                  }
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}


          {/* Future Enterprise Modules */}
          <div className="pt-2 border-t border-white/5 space-y-1">
            <button
              onClick={() => setShowFutureModules(!showFutureModules)}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-[10px] uppercase tracking-widest text-white/30 hover:text-white/60 font-mono transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-cyan-400/60" /> Future Modules
              </span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showFutureModules ? 'rotate-180' : ''}`} />
            </button>
            {showFutureModules && (
              <div className="space-y-1 pt-1 pl-2">
                {futureModules.map((m, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg text-xs text-white/25 bg-white/[0.02] border border-white/5 cursor-not-allowed">
                    <span>{m}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-white/40 font-mono">Coming Soon</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="p-4 border-t border-white/10 bg-black/20">
          <div className="mb-3 px-3 py-2 rounded-lg bg-cyan-950/30 border border-cyan-500/20 text-[11px] font-mono text-cyan-300/80 truncate">
            <span className="block text-[9px] text-white/40 uppercase">Logged in as</span>
            {admin?.email || "careers@quanentry.com"}
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-red-300 hover:bg-red-500/10 transition-all border border-transparent hover:border-red-500/20 cursor-pointer"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout Administrator</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto flex flex-col justify-between">
        <div className="p-8 flex-1">
          <Outlet />
        </div>

        {/* Institutional Build & Deployment Footer */}
        <footer className="px-8 py-4 border-t border-white/5 bg-slate-950/80 backdrop-blur-md text-[11px] font-mono text-cyan-300/60 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white font-medium">Quanentry Capital Portal</span>
            <span>• Version {BUILD_META.version}</span>
          </div>
          <div className="flex items-center gap-4 text-[10px] text-white/40 flex-wrap justify-center">
            <span>Frontend: <strong className="text-cyan-300/80">{BUILD_META.formatted_time}</strong> ({BUILD_META.build_id})</span>
            <span>•</span>
            <span>Backend: <strong className="text-cyan-300/80">{BUILD_META.formatted_time}</strong> (be-v{BUILD_META.version})</span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default AdminLayout;

